<template>
  <div :class="cardClasses">
    <div v-if="$slots.header || title" class="base-card__header">
      <slot name="header">
        <h3 v-if="title" class="base-card__title">{{ title }}</h3>
      </slot>
    </div>
    
    <div v-if="imageSrc" class="base-card__image">
      <img :src="imageSrc" :alt="imageAlt" />
      <div v-if="$slots.overlay" class="base-card__overlay">
        <slot name="overlay" />
      </div>
    </div>
    
    <div class="base-card__body">
      <slot />
    </div>
    
    <div v-if="$slots.footer" class="base-card__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseCard',
  props: {
    title: {
      type: String,
      default: null
    },
    imageSrc: {
      type: String,
      default: null
    },
    imageAlt: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'elevated', 'outlined', 'flat'].includes(value)
    },
    hoverable: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    },
    horizontal: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cardClasses() {
      return [
        'base-card',
        `base-card--${this.variant}`,
        {
          'base-card--hoverable': this.hoverable,
          'base-card--clickable': this.clickable,
          'base-card--horizontal': this.horizontal
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.base-card {
  @include card-base;
  display: flex;
  flex-direction: column;
  position: relative;
  
  &--default {
    background-color: $white;
    box-shadow: $shadow-md;
  }
  
  &--elevated {
    background-color: $white;
    box-shadow: $shadow-lg;
  }
  
  &--outlined {
    background-color: $white;
    box-shadow: none;
    border: 1px solid $gray-200;
  }
  
  &--flat {
    background-color: $gray-100;
    box-shadow: none;
  }
  
  &--hoverable {
    @include card-hover;
  }
  
  &--clickable {
    cursor: pointer;
    
    &:active {
      transform: scale(0.98);
    }
  }
  
  &--horizontal {
    flex-direction: row;
    
    .base-card__image {
      flex: 0 0 40%;
      max-width: 40%;
      border-radius: $radius-lg 0 0 $radius-lg;
    }
    
    .base-card__body {
      flex: 1;
    }
    
    @include media-breakpoint-down(sm) {
      flex-direction: column;
      
      .base-card__image {
        flex: none;
        max-width: 100%;
        border-radius: $radius-lg $radius-lg 0 0;
      }
    }
  }
  
  &__header {
    padding: $spacing-lg $spacing-lg 0;
    
    &:last-child {
      padding-bottom: $spacing-lg;
    }
  }
  
  &__title {
    font-size: $font-size-xl;
    font-weight: 600;
    color: $primary-color;
    margin: 0;
  }
  
  &__image {
    position: relative;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform $transition-base;
    }
    
    &:hover img {
      transform: scale(1.05);
    }
  }
  
  &__overlay {
    @include overlay(rgba($black, 0.4));
    @include flex-center;
    opacity: 0;
    transition: opacity $transition-base;
    
    .base-card:hover & {
      opacity: 1;
    }
  }
  
  &__body {
    padding: $spacing-lg;
    flex: 1;
    
    .base-card__header + & {
      padding-top: $spacing-md;
    }
    
    .base-card__image + & {
      padding-top: $spacing-lg;
    }
  }
  
  &__footer {
    padding: 0 $spacing-lg $spacing-lg;
    border-top: 1px solid $gray-200;
    
    .base-card__body + & {
      padding-top: $spacing-lg;
      margin-top: auto;
    }
  }
}</style>